import React, { useMemo, useState } from 'react';
import theme from '@/constants/theme';
import { styles } from './styles';
import { useLoadAudio } from './useLoadAudio';
import {
  Audiotrack,
  Forward10,
  Loop,
  Pause,
  PlayArrow,
  Replay,
  Replay10,
  VolumeMute,
  VolumeUp,
} from '@mui/icons-material';
import { Teleprompter } from './Teleprompter';
import Spinner from '@/components/dataDisplay/Spinner';
interface Props {
  src?: string;
  subtitles?: any[];
}

export const Audio: React.FC<Props> = ({
  src = 'https://cdn.pixabay.com/audio/2025/03/12/audio_7cef52a398.mp3',
  subtitles,
}) => {
  const { audio, audioRef, isPlaying, loading, position, duration } = useLoadAudio({
    uri: src,
  });
  const isMobile = useMemo(() => window.innerWidth < 600, []);

  const [showVolume, setShowVolume] = useState(false);
  const [volume, setVolume] = useState(100);

  const handleShowVolume = () => {
    setShowVolume(!showVolume);
  };

  const replayAudio = () => {
    if (audio) {
      audio.currentTime = 0;
      audio.play();
    }
  };

  const togglePlayPause = () => {
    if (!audio) return;
    if (duration === position) {
      replayAudio();
      return;
    }
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const skip = (ms: number) => {
    if (audio) {
      const current = audio.currentTime * 1000;
      const newPos = Math.min(Math.max(current + ms, 0), audio.duration * 1000);
      audio.currentTime = newPos / 1000;
    }
  };

  const adjustVolume = async (value: number) => {
    setVolume(value);
    if (audio) {
      audio.volume = value / 100;
    }
  };

  const adjustPosition = (value: number) => {
    if (audio) {
      audio.currentTime = value / 1000;
    }
  };

  const displayTime = useMemo(() => {
    const newPosition = Math.floor(position / 1000)
      .toString()
      .padStart(2, '0');
    const newDuration = Math.floor(duration / 1000);
    return `${newPosition}s : ${newDuration}s`;
  }, [position, duration]);

  const isMuted = useMemo(() => {
    return volume === 0;
  }, [volume]);

  const hasFinished = useMemo(() => {
    return position === duration;
  }, [position, duration]);

  return (
    <div style={styles.container}>
      <div style={styles.audioCover}>
        {subtitles && subtitles.length > 0 ? (
          <Teleprompter audioRef={audioRef} sentences={subtitles} />
        ) : (
          <Audiotrack style={styles.audioTrackIcon} />
        )}
      </div>
      {loading ? (
        <div style={styles.loaderContainer}>
          <Spinner style={styles.loader} />
        </div>
      ) : (
        <div style={styles.controls}>
          <p style={styles.timeIndicator}>{displayTime}</p>
          <input
            type="range"
            min={0}
            max={duration}
            value={position}
            style={{ accentColor: theme.colors.primary }}
            onChange={(e) => adjustPosition(Number(e.target.value))}
          />
          <div style={styles.controlsOptions}>
            <button onClick={replayAudio} style={styles.button}>
              <Loop fontSize={isMobile ? 'medium' : 'large'} />
            </button>
            <div style={styles.playerOptions}>
              <button onClick={() => skip(-10000)} style={styles.button}>
                <Replay10 fontSize={isMobile ? 'medium' : 'large'} />
              </button>
              <button onClick={togglePlayPause} style={styles.playButton}>
                {isPlaying ? (
                  <Pause fontSize={isMobile ? 'medium' : 'large'} />
                ) : hasFinished ? (
                  <Replay fontSize={isMobile ? 'medium' : 'large'} />
                ) : (
                  <PlayArrow fontSize={isMobile ? 'medium' : 'large'} />
                )}
              </button>
              <button onClick={() => skip(10000)} style={styles.button}>
                <Forward10 fontSize={isMobile ? 'medium' : 'large'} />
              </button>
            </div>
            <button onClick={handleShowVolume} style={styles.button}>
              {isMuted ? (
                <VolumeMute fontSize={isMobile ? 'medium' : 'large'} />
              ) : (
                <VolumeUp fontSize={isMobile ? 'medium' : 'large'} />
              )}
            </button>
          </div>
          {showVolume && (
            <div style={styles.volumeOptions}>
              {isMuted ? (
                <VolumeMute fontSize={isMobile ? 'medium' : 'large'} />
              ) : (
                <VolumeUp fontSize={isMobile ? 'medium' : 'large'} />
              )}
              <input
                type="range"
                min={0}
                max={100}
                value={volume}
                style={{ flex: 1, accentColor: theme.colors.primary }}
                onChange={(e) => adjustVolume(Number(e.target.value))}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
